import { useEffect, useContext, useState, type FunctionComponent, type ReactNode } from "react";
import { makeStyles, shorthands, tokens, Toaster, mergeClasses } from "@fluentui/react-components";
import { useLocation } from "react-router-dom";
import BackNavigation from "./shared/BackNavigation";
import { humanize } from "@src/taskpane/helpers/formatData";
import { WalterContext } from "@src/taskpane/providers/walter";
import { useOfficeInitialized } from "@src/taskpane/hooks/useOfficeInitialized";
import { useAppReady } from "@src/taskpane/hooks/useAppReady";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import { LoginPage } from "./LoginPage";
import Progress from "@src/taskpane/components/Progress";
import { migrate } from "@src/lib/migrations/migrate";
import { useTypeOptions } from "@src/taskpane/hooks/useTypeOptions";
import { ViewToggle, type ViewMode } from "./chat/ViewToggle";
import { ChatView } from "./chat/ChatView";
import { useFeatureFlags } from "@src/taskpane/hooks/useFeatureFlags";

const useOuterStyles = makeStyles({
  container: {
    minHeight: "100vh",
  },
  centeredContainer: {
    display: "grid",
    alignItems: "center",
    justifyItems: "center",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    boxSizing: "border-box",
    "> :not(:first-child)": {
      marginTop: "0px",
    },
    "& button": {
      width: "100%",
    },
  },
  backButton: {
    ...shorthands.margin("12px", "12px", "0", "12px"),
  },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke1),
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
  },
});

const useInnerStyles = makeStyles({
  paddedContent: {
    ...shorthands.padding("0", "12px"),
  },
});

const ModeBanner = () => {
  let backgroundColor = tokens.colorPaletteBerryBackground2;
  let environment: string;

  if (window.location.hostname === "word.staging.getwalter.com") {
    environment = "staging";
    backgroundColor = "lightgreen";
  } else {
    environment = import.meta.env.MODE;
  }

  return <div style={{ backgroundColor, textAlign: "center", padding: "8px" }}>{humanize(environment)}</div>;
};

const Main = ({ children, padContent }: { children: ReactNode; padContent: boolean }) => {
  const innerStyles = useInnerStyles();
  useTypeOptions();

  return padContent ? <div className={innerStyles.paddedContent}>{children}</div> : <div>{children}</div>;
};

const VIEW_MODE_STORAGE_KEY = "word-addin-view-mode";

const Layout: FunctionComponent = ({ children }) => {
  const outerStyles = useOuterStyles();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isChoosingFieldSet = location.pathname === "/choose-field-set";
  const { toasterId, isLoggedIn } = useContext(WalterContext);
  const officeReady = useOfficeInitialized();
  const { isAppReady: appReady } = useAppReady();
  const featureFlagManager = useFeatureFlags();

  // Feature flag for automation mode in Word add-in (off by default)
  const [isAutomationModeEnabled, setIsAutomationModeEnabled] = useState<boolean | null>(null);

  // Check feature flag when logged in
  useEffect(() => {
    if (isLoggedIn) {
      featureFlagManager
        .isEnabled("word_add_in_automation_mode")
        .then(setIsAutomationModeEnabled)
        .catch(() => setIsAutomationModeEnabled(false));
    }
  }, [isLoggedIn, featureFlagManager]);

  // View mode state with localStorage persistence
  // Default to chat mode; only allow automation mode if the feature flag is enabled
  const getSavedViewMode = (): ViewMode => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved === "chat" || saved === "automation") {
      return saved;
    }
    return "chat"; // Default to chat mode
  };

  const [viewMode, setViewMode] = useState<ViewMode>(getSavedViewMode);
  // If automation mode is not enabled, always force chat mode
  const effectiveViewMode = isAutomationModeEnabled ? viewMode : "chat";

  // Save the effective view mode to localStorage so it persists across sessions
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, effectiveViewMode);
  }, [effectiveViewMode]);

  // Run migrations again once we have a token (this allows migrations to defer themselves until we have a token)
  useEffect(() => {
    if (appReady && isLoggedIn) {
      void migrate("api").catch(captureErrorInfo);
    }
  }, [appReady, isLoggedIn]);

  let banner;

  if (["word.staging.getwalter.com", "localhost"].includes(window.location.hostname)) {
    banner = <ModeBanner />;
  }

  if (!officeReady) {
    return (
      <div className={mergeClasses(outerStyles.container, outerStyles.centeredContainer)}>
        <Progress message="Initializing Office..." />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginPage>{banner}</LoginPage>;
  }

  // In chat mode, render only the ChatView - full takeover
  if (effectiveViewMode === "chat") {
    return (
      <ChatView
        onSwitchToAutomation={isAutomationModeEnabled ? () => setViewMode("automation") : undefined}
        automationModeEnabled={isAutomationModeEnabled ?? false}
      />
    );
  }

  return (
    <div className={outerStyles.container}>
      {banner}
      <Toaster toasterId={toasterId} position="top" pauseOnHover pauseOnWindowBlur />

      {/* Automation view */}
      <div className={outerStyles.root}>
        <div className={outerStyles.headerBar}>
          <div className={outerStyles.headerLeft}>{!isHome && <BackNavigation />}</div>
          <div className={outerStyles.headerRight}>
            <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
          </div>
        </div>
        <Main padContent={!(isHome || isChoosingFieldSet)}>{children}</Main>
      </div>
    </div>
  );
};

export default Layout;
